<template>
  <div :class="[themeClass, 'app', { 'app--no-top-padding': isInlineHome, 'app--signed-in': isSignedInHome }]">
    <AppTopBar v-if="showTopBar" />
    <router-view />
    <AppSnackbar />
    <CookieBanner />
    <CookieSettings />
    <SeatLockOverlay />
  </div>
</template>

<script>
import { authState } from "./stores/authStore";
import AppTopBar from "./components/AppTopBar.vue";
import AppSnackbar from "./components/AppSnackbar.vue";
import CookieBanner from "./components/CookieBanner.vue";
import CookieSettings from "./components/CookieSettings.vue";
import SeatLockOverlay from "./components/SeatLockOverlay.vue";

export default {
  components: { AppTopBar, AppSnackbar, CookieBanner, CookieSettings, SeatLockOverlay },
  data() {
    return {
      systemPrefersDark: false,
      themeMediaQuery: null,
      themeMediaQueryHandler: null,
    };
  },
  computed: {
    resolvedTheme() {
      return this.systemPrefersDark ? "dark" : "light";
    },
    showTopBar() {
      // Hidden on routes that opt out (SignIn, NoteDetail, Profile, etc.) and
      // on the authenticated dashboard (Home replaced by its own header).
      if (this.$route.meta?.hideGlobalLogo) return false;
      if (this.$route.name === "Home" && authState.isAuthenticated) return false;
      return true;
    },
    isSignedInHome() {
      // Every signed-in screen sits on the iOS grouped page colour.
      if (this.$route.name === "NoteDetail" || this.$route.name === "Profile") return true;
      return this.$route.name === "Home" && authState.isAuthenticated;
    },
    isInlineHome() {
      // Routes that paint their own full-bleed header (authenticated
      // Home, Analytics, NoteDetail, Profile, OAuth callbacks) drop the
      // wrapper's top padding so their header sits flush at the
      // viewport edge instead of being pushed down 30px.
      if (this.$route.meta?.hideGlobalLogo) return true;
      return this.$route.name === "Home" && authState.isAuthenticated;
    },
    themeClass() {
      return `${this.resolvedTheme}-mode`;
    },
  },
  created() {
    if (typeof window !== "undefined") {
      this.themeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      this.systemPrefersDark = this.themeMediaQuery.matches;
      this.themeMediaQueryHandler = (event) => {
        this.systemPrefersDark = event.matches;

        this.applyTheme();
      };

      if (typeof this.themeMediaQuery.addEventListener === "function") {
        this.themeMediaQuery.addEventListener("change", this.themeMediaQueryHandler);
      } else if (typeof this.themeMediaQuery.addListener === "function") {
        this.themeMediaQuery.addListener(this.themeMediaQueryHandler);
      }
    }

    this.applyTheme();
  },
  methods: {
    applyTheme() {
      if (typeof document === "undefined") {
        return;
      }

      const theme = this.resolvedTheme;
      const themeColor = theme === "dark" ? "#08111f" : "#f5f7fb";

      document.documentElement.dataset.theme = theme;
      document.documentElement.style.colorScheme = theme;

      const themeColorMeta = document.head.querySelector('meta[name="theme-color"]');

      if (themeColorMeta) {
        themeColorMeta.setAttribute("content", themeColor);
      }
    },
  },
  beforeUnmount() {
    if (!this.themeMediaQuery || !this.themeMediaQueryHandler) {
      return;
    }

    if (typeof this.themeMediaQuery.removeEventListener === "function") {
      this.themeMediaQuery.removeEventListener("change", this.themeMediaQueryHandler);
    } else if (typeof this.themeMediaQuery.removeListener === "function") {
      this.themeMediaQuery.removeListener(this.themeMediaQueryHandler);
    }
  },
};
</script>

<style scoped>
.app {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 18px;
  min-height: 100vh;
  padding: 30px 0 0;
  background:
    radial-gradient(circle at top, var(--app-glow), transparent 42%),
    var(--app-bg);
  color: var(--app-text);
}

/* Authenticated home: dashboard renders inline, so drop the wrapper's
   top padding/gap to let the brand header sit at the top of the viewport. */
.app.app--no-top-padding {
  padding-top: 0;
  gap: 0;
  justify-content: flex-start;
}

/* The signed-in screens sit on the iOS grouped page colour. */
.app.app--signed-in {
  background: var(--wn-page);
}

.theme-toggle {
  display: none;
}

:global(html),
:global(body) {
  min-height: 100%;
  background: var(--app-bg);
  color: var(--app-text);
  font-family: var(--app-font);
}

:global(body) {
  margin: 0;
}

/* shadcn-inspired neutral palette using HSL tokens for finer control. */
:global(html[data-theme="light"]) {
  --app-bg: hsl(0 0% 100%);
  --app-surface: hsl(0 0% 100%);
  --app-surface-strong: hsl(210 40% 98%);
  --app-border: hsl(214.3 31.8% 91.4%);
  --app-text: hsl(222.2 47.4% 11.2%);
  --app-text-muted: hsl(215.4 16.3% 46.9%);
  --app-heading: hsl(222.2 84% 4.9%);
  --app-link: hsl(221.2 83.2% 53.3%);
  --app-link-hover: hsl(221.2 83.2% 43.3%);
  --app-accent: hsl(221.2 83.2% 53.3%);
  --app-glow: hsl(0 0% 100% / 0.7);
  --app-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 12px rgba(15, 23, 42, 0.06);
  --app-radius: 10px;
  --app-radius-lg: 14px;
  --app-font:
    "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color-scheme: light;
  /* The signed-in app, mirroring the iOS app: system yellow accent, grouped
     grey page, white 16px row cards, gray secondary text. */
  --wn-accent: #ffcc00;
  --wn-accent-ink: #1c1c1e;
  --wn-accent-soft: rgba(255, 204, 0, 0.18);
  --wn-accent-text: #a07800;
  --wn-page: #f3f2f8;
  --wn-card: #ffffff;
  --wn-card-hover: #f7f7fa;
  --wn-segment: #ffffff;
  /* iOS StudyActionButton: yellow with white text in light mode… */
  --wn-study-bg: #ffcc00;
  --wn-study-ink: #ffffff;
  --wn-grouped: #f2f2f7;
  --wn-fill: rgba(118, 118, 128, 0.12);
  --wn-separator: rgba(60, 60, 67, 0.18);
  --wn-label: #000000;
  --wn-secondary: rgba(60, 60, 67, 0.62);
  --wn-tertiary: rgba(60, 60, 67, 0.32);
  --wn-radius: 16px;
}

:global(html[data-theme="dark"]) {
  --app-bg: #000000;
  --app-surface: hsl(0 0% 6%);
  --app-surface-strong: hsl(0 0% 9%);
  --app-border: hsl(0 0% 16%);
  --app-text: hsl(0 0% 96%);
  --app-text-muted: hsl(0 0% 65%);
  --app-heading: hsl(0 0% 98%);
  --app-link: hsl(217.2 91.2% 64%);
  --app-link-hover: hsl(217.2 91.2% 74%);
  --app-accent: hsl(217.2 91.2% 64%);
  --app-glow: hsl(0 0% 100% / 0.04);
  --app-shadow:
    0 1px 2px rgba(0, 0, 0, 0.5),
    0 8px 24px rgba(0, 0, 0, 0.5);
  --app-radius: 10px;
  --app-radius-lg: 14px;
  --app-font:
    "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color-scheme: dark;
  --wn-accent: #ffd60a;
  --wn-accent-ink: #1c1c1e;
  --wn-accent-soft: #322e1b;
  --wn-accent-text: #ffd60a;
  --wn-page: #000000;
  --wn-card: #1a1a1c;
  --wn-card-hover: #232326;
  --wn-segment: #636366;
  /* …a dark olive with yellow text in dark mode. */
  --wn-study-bg: #322e1b;
  --wn-study-ink: #ffd60a;
  --wn-grouped: #2c2c2e;
  --wn-fill: rgba(118, 118, 128, 0.24);
  --wn-separator: rgba(84, 84, 88, 0.6);
  --wn-label: #ffffff;
  --wn-secondary: rgba(235, 235, 245, 0.6);
  --wn-tertiary: rgba(235, 235, 245, 0.3);
  --wn-radius: 16px;
}
</style>
